<template>
    <div class="tw-grid tw-auto-rows-max tw-gap-8 tw-grid-cols-2 tw-mt-8">
        <div class="tw-col-span-1">
            <l-multi-filter
                saved-view-type="feedbackAnalytics"
                :filter-types="filterTypes"
                :show-custom-attributes="true"
                :preselected-filters="selectedFilters"
                :auto-apply-preselected-filters="true"
                @update="applyFilters"
            />
        </div>

        <l-text-alert
            v-if="hasLoadingError"
            type="danger"
            :text="$t('feedback.analytics.loadingError')"
            class="tw-col-span-2"
        />

        <div class="tw-col-span-2 tw-text-gray-400">
            <span v-if="rights.F_ANALYTICS_COMPANY">
                {{ $t("feedback.analytics.accessToCompany") }}
            </span>
            <span v-else-if="rights.F_ANALYTICS_LOCAL_TEAMS">
                {{ $t("feedback.analytics.accessToLocalTeams") }}
            </span>
            <span v-else>
                {{ $t("feedback.analytics.accessToReports") }}
            </span>
        </div>

        <div
            class="tw-col-span-2"
            :class="{
                'tw-border-0 tw-border-b tw-border-gray-100 tw-border-solid tw-pb-16':
                    hasSkillInsights || hasBadgeInsights,
            }"
        >
            <l-content-box>
                <l-feedback-analytics-totals v-if="analytics.stats" :stats="analytics.stats" />
                <l-skeleton-loader v-else />
            </l-content-box>
        </div>

        <template v-if="hasSkillInsights || hasBadgeInsights">
            <div
                v-if="hasSkillInsights"
                class="tw-col-span-2"
                :class="{
                    'xl:tw-col-span-1': hasBadgeInsights,
                    'xl:tw-col-span-2': !hasBadgeInsights,
                }"
            >
                <l-content-box :is-full-height="true">
                    <template #headline>
                        {{ $t("feedback.analytics.skillInsights.title") }}
                        <span class="tw-text-sm tw-leading-7 tw-font-bold tw-text-gray-400">
                            ( {{ $t("feedback.analytics.labels.top") }}
                            {{ analytics.insights[visibleSkillType].length }} )
                        </span>
                    </template>
                    <template #headlineSupport>
                        <a
                            href="#"
                            class="tw-flex-1 tw-text-right tw-text-sm tw-text-gray-500 tw-cursor-pointer"
                            @click.prevent="
                                visibleSkillType === 'skillInsights'
                                    ? (visibleSkillType = 'skillCategoryInsights')
                                    : (visibleSkillType = 'skillInsights')
                            "
                            >{{
                                $t(
                                    `feedback.analytics.skillInsights.switchFrom.${visibleSkillType}`,
                                )
                            }}<i class="far fa-exchange tw-ml-2"
                        /></a>
                    </template>

                    <l-feedback-analytics-skill-insights
                        :insights="analytics.insights[visibleSkillType]"
                    />
                </l-content-box>
            </div>

            <div
                v-if="hasBadgeInsights"
                class="tw-col-span-2"
                :class="{
                    'xl:tw-col-span-1': hasSkillInsights,
                    'xl:tw-col-span-2': !hasSkillInsights,
                }"
            >
                <l-content-box>
                    <template #headline>
                        {{ $t("feedback.analytics.badgeInsights.title") }}
                        <span class="tw-text-sm tw-leading-7 tw-font-bold tw-text-gray-400">
                            ( {{ $t("feedback.analytics.labels.top") }}
                            {{ visibleBadgeInsights.length }} )
                        </span>
                    </template>
                    <template #headlineSupport>
                        <div
                            v-b-popover.top.hover="$t('feedback.analytics.badgeInsights.hint')"
                            class="tw-flex-grow-0 tw-text-right tw-text-base tw-text-gray-500"
                        >
                            <i class="fal fa-question-circle tw-ml-1" />
                        </div>
                    </template>

                    <l-feedback-analytics-badge-distribution
                        :badges="visibleBadgeInsights"
                        :selected-badge="sort.targetBadge"
                        :loading="loading.userList"
                        @sortChange="onSortChange"
                    />
                </l-content-box>
            </div>
        </template>

        <div
            class="tw-col-span-2 tw-border-0 tw-border-t tw-border-gray-100 tw-border-solid tw-pt-16"
        >
            <l-content-box>
                <template #headline>
                    {{ $t("feedback.analytics.userlist.title") }}
                    <span class="tw-text-sm tw-leading-7 tw-font-bold tw-text-gray-400">
                        (
                        <template v-if="!loading.userList"
                            >{{ $t("feedback.analytics.labels.top") }}
                            {{ sortedUserList.length }}</template
                        ><template v-else>{{ $t("feedback.analytics.labels.loading") }}</template> )
                    </span>
                </template>
                <template #headlineSupport>
                    <l-feedback-analytics-user-list-sort
                        ref="filter"
                        :badges="badgeHashMap"
                        :loading="loading.userList"
                        :sort="sort"
                        @sortChange="onSortChange"
                    />
                </template>

                <template
                    v-if="
                        (requiresDataRefetchForSorting && !loading.userList) ||
                            (!requiresDataRefetchForSorting && initialLoadDone.userList)
                    "
                >
                    <l-feedback-analytics-user-list
                        :badges="badgeHashMap"
                        :users="sortedUserList"
                        :loading="loading.userList"
                        :sort="sort"
                        @sortChange="onSortChange"
                    />
                </template>
                <l-skeleton-loader v-else />
            </l-content-box>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LMultiFilter from "@/components/LMultiFilter";
import LFeedbackAnalyticsTotals from "@/components/LFeedbackAnalytics/LFeedbackAnalyticsTotals";
import LFeedbackAnalyticsSkillInsights from "@/components/LFeedbackAnalytics/LFeedbackAnalyticsSkillInsights";
import LFeedbackAnalyticsBadgeDistribution from "@/components/LFeedbackAnalytics/LFeedbackAnalyticsBadgeDistribution";
import LFeedbackAnalyticsUserList from "@/components/LFeedbackAnalytics/LFeedbackAnalyticsUserList";
import LFeedbackAnalyticsUserListSort from "@/components/LFeedbackAnalytics/LFeedbackAnalyticsUserListSort";

import LContentBox from "@/components/LContentBox";
import LSkeletonLoader from "@/components/LSkeletonLoader";
import LTextAlert from "@/components/LTextAlert";

const maxAmountOfUsersInToplist = 500;
const maxAmountOfInsightItems = 8;

export default {
    components: {
        LMultiFilter,
        LFeedbackAnalyticsTotals,
        LFeedbackAnalyticsSkillInsights,
        LFeedbackAnalyticsBadgeDistribution,
        LFeedbackAnalyticsUserList,
        LContentBox,
        LSkeletonLoader,
        LFeedbackAnalyticsUserListSort,
        LTextAlert,
    },
    data() {
        return {
            loading: {
                userList: false,
            },
            initialLoadDone: {
                userList: false,
            },
            hasLoadingError: false,
            visibleSkillType: "skillInsights",
            sort: {
                path: this.$route.query.path || "received",
                direction: this.$route.query.direction || "desc",
                targetBadge: this.$route.query.targetBadge || null,
            },
            filters: {},
            selectedFilters: {},
        };
    },
    computed: {
        ...mapGetters({
            userList: "feedback/stats/userList",
            analytics: "feedback/stats/analytics",
            rights: "users/current/rights",
        }),
        hasSkillInsights() {
            return this.analytics?.insights?.skillInsights?.[0]?.amount > 0;
        },
        hasBadgeInsights() {
            return this.analytics?.insights?.badgeInsights?.[0]?.amount > 0;
        },
        visibleBadgeInsights() {
            if (!this.hasBadgeInsights) return [];

            return this.analytics?.insights?.badgeInsights?.slice(0, maxAmountOfInsightItems);
        },
        requiresDataRefetchForSorting() {
            return (
                Array.isArray(this.userList) && this.userList.length >= maxAmountOfUsersInToplist
            );
        },
        sortedUserList() {
            if (this.requiresDataRefetchForSorting) {
                return this.userList;
            } else {
                return Object.freeze(
                    [...this.userList].sort(
                        this.compareUsers({
                            sortPath: this.sort.path,
                            sortDirection: this.sort.direction,
                            sortTargetBadge: this.sort.targetBadge,
                        }),
                    ),
                );
            }
        },
        badgeHashMap() {
            let badges = {};
            if (!this.hasBadgeInsights) return badges;

            // for easy access in user list
            for (let badge of this.analytics.insights.badgeInsights) {
                badges[badge._id] = badge;
            }

            return badges;
        },
        combinedFilters() {
            return {
                filters: this.filters,
                sortPath: this.sort.path,
                sortDirection: this.sort.direction,
                sortTargetBadge: this.sort.targetBadge,
            };
        },
        filterTypes() {
            return [
                {
                    value: "localTeams",
                    key: "localTeams",
                    text: this.$t(`multiFilter.selectLocalTeams`),
                },
                {
                    value: "managers",
                    key: "managers",
                    text: this.$t(`multiFilter.selectManagers`),
                },
                {
                    value: "users",
                    key: "users",
                    text: this.$t(`multiFilter.selectUsers`),
                },
                {
                    value: "locations",
                    key: "locations",
                    text: this.$t(`multiFilter.selectLocations`),
                },
                {
                    value: "periods",
                    key: "periods",
                    text: this.$t(`multiFilter.selectTimeframe`),
                },
                {
                    value: "admin",
                    key: "adminFilter",
                    text: this.$t("multiFilter.selectAdmin"),
                    options: [
                        {
                            _id: "indirectReportsSearch",
                            name: this.$t(`multiFilter.optionIndirectReports`),
                        },
                    ],
                },
            ];
        },
    },
    watch: {
        sort: {
            deep: true,
            async handler(newValue) {
                this.$router.push({ query: Object.assign({}, this.$route.query, newValue) });

                if (!this.requiresDataRefetchForSorting || this.loading.userList) return;

                try {
                    this.hasLoadingError = false;
                    await this.getUserlistData();
                } catch {
                    this.hasLoadingError = true;
                }
            },
        },
    },
    created() {
        this.selectedFilters = { admin: ["indirectReportsSearch"] };
        this.loadData({ reset: true });
    },
    methods: {
        applyFilters(filters) {
            this.filters = filters;
            this.defaultSomeSortValues();
            this.loadData();
        },
        async loadData({ reset = false } = {}) {
            if (
                (this.loading.userList && this.initialLoadDone.userList) ||
                (reset && this.loading.userList)
            ) {
                // wait for loading
                return;
            }

            // indicate loading on manual trigger
            if (reset) this.resetData();

            try {
                this.hasLoadingError = false;
                await Promise.all([this.getUserlistData(), this.getInsightData()]);
            } catch {
                this.hasLoadingError = true;
            }
        },
        resetData() {
            this.loading = {
                userList: true,
            };
            this.initialLoadDone = {
                userList: false,
            };
        },
        async getUserlistData() {
            this.loading.userList = true;
            await this.$store
                .dispatch({
                    type: "feedback/stats/getUserlist",
                    payload: { query: this.combinedFilters },
                })
                .finally(() => {
                    this.loading.userList = false;
                    this.initialLoadDone.userList = true;
                });
        },
        async getInsightData() {
            await this.$store.dispatch({
                type: "feedback/stats/getAnalytics",
                payload: { query: this.combinedFilters },
            });
        },
        onSortChange(type, badgeId) {
            if (type === "badge" && badgeId) this.sort.targetBadge = badgeId;

            if (this.sort.path === type) {
                this.sort.direction = this.sort.direction === "desc" ? "asc" : "desc";
            } else {
                this.sort.direction = "desc";
                this.sort.path = type;
            }
        },
        defaultSomeSortValues() {
            // badge might not be available anymore
            this.sort.targetBadge = null;
            if (this.sort.path === "badge") this.onSortChange("received");
        },
        compareUsers({ sortPath, sortDirection, sortTargetBadge }) {
            return (a, b) => {
                let amountA = a[sortPath] || a.badges?.[sortTargetBadge] || 0;
                let amountB = b[sortPath] || b.badges?.[sortTargetBadge] || 0;

                if (amountA < amountB) return sortDirection === "desc" ? 1 : -1;
                if (amountA > amountB) return sortDirection === "desc" ? -1 : 1;

                return a.user?.fullname?.localeCompare(b.user?.fullname) || 0;
            };
        },
    },
};
</script>

<style>
html {
    overflow-y: scroll;
}
</style>
